/**
 * JPEG EXIF의 촬영일시를 읽는다 — 라이브러리 없이 APP1 세그먼트만 최소 파싱.
 *
 * 우선순위: DateTimeOriginal(0x9003, ExifIFD) → DateTime(0x0132, IFD0).
 * EXIF가 없거나(스크린샷·PNG·메신저 전달본 등) 파싱 실패면 **null**을 반환한다 —
 * 호출부는 null이면 '통과'로 처리한다(촬영일 판별 불가 사진은 막지 않음).
 *
 * iOS가 웹 업로드 시 HEIC→JPEG 재인코딩하며 EXIF를 잃는 경우도 null → 통과.
 */
export async function readExifDate(file: File): Promise<Date | null> {
  try {
    // EXIF는 파일 앞부분(APP1)에 있다 — 앞 256KB만 읽는다.
    const buf = await file.slice(0, 256 * 1024).arrayBuffer();
    const view = new DataView(buf);
    if (view.byteLength < 4 || view.getUint16(0) !== 0xffd8) return null; // JPEG SOI 아님
    let offset = 2;
    const len = view.byteLength;
    while (offset + 4 <= len) {
      const marker = view.getUint16(offset);
      if ((marker & 0xff00) !== 0xff00) break; // 마커 아님 → 스캔 데이터 시작
      const size = view.getUint16(offset + 2);
      if (marker === 0xffe1) {
        // APP1 — "Exif\0\0" 확인
        if (
          offset + 10 <= len &&
          view.getUint32(offset + 4) === 0x45786966 && // 'Exif'
          view.getUint16(offset + 8) === 0x0000
        ) {
          return parseTiffDate(view, offset + 10);
        }
      }
      if (size < 2) break;
      offset += 2 + size;
    }
    return null;
  } catch {
    return null;
  }
}

/** TIFF 헤더(EXIF 본문)에서 촬영일 문자열을 찾아 Date로 변환. */
function parseTiffDate(view: DataView, tiff: number): Date | null {
  try {
    const bo = view.getUint16(tiff);
    const le = bo === 0x4949; // 'II' little-endian / 'MM' big-endian
    const u16 = (o: number) => view.getUint16(o, le);
    const u32 = (o: number) => view.getUint32(o, le);
    if (u16(tiff + 2) !== 0x002a) return null; // TIFF magic
    const ifd0 = tiff + u32(tiff + 4);

    const findTag = (ifd: number, tag: number): number | null => {
      const count = u16(ifd);
      for (let i = 0; i < count; i += 1) {
        const entry = ifd + 2 + i * 12;
        if (u16(entry) === tag) return entry;
      }
      return null;
    };
    const readAscii = (entry: number): string | null => {
      if (u16(entry + 2) !== 2) return null; // ASCII 타입 아님
      const cnt = u32(entry + 4);
      if (cnt < 2 || cnt > 64) return null;
      const valOffset = cnt > 4 ? tiff + u32(entry + 8) : entry + 8;
      let s = '';
      for (let i = 0; i < cnt - 1; i += 1) s += String.fromCharCode(view.getUint8(valOffset + i));
      return s;
    };

    let str: string | null = null;
    const exifPtr = findTag(ifd0, 0x8769); // ExifIFD 포인터
    if (exifPtr) {
      const exifIfd = tiff + u32(exifPtr + 8);
      const dto = findTag(exifIfd, 0x9003); // DateTimeOriginal
      if (dto) str = readAscii(dto);
    }
    if (!str) {
      const dt = findTag(ifd0, 0x0132); // DateTime (촬영일 대체)
      if (dt) str = readAscii(dt);
    }
    if (!str) return null;

    // "YYYY:MM:DD HH:MM:SS" (구분자 관대하게)
    const m = str.match(/^(\d{4})\D(\d{2})\D(\d{2})\D(\d{2})\D(\d{2})\D(\d{2})/);
    if (!m) return null;
    const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]), Number(m[4]), Number(m[5]), Number(m[6]));
    return Number.isNaN(d.getTime()) ? null : d;
  } catch {
    return null;
  }
}
